import { z } from "zod";
import { BoundingBox, CONTRACT_VERSION } from "./common.js";
/**
 * Drapeaux de fonctionnalité par réseau (CDC §7.1).
 * `driverIdentity` et `messaging` n'existent pas : il n'y a rien à activer.
 */
export const FeatureFlags = z.object({
    vehicleRunMatching: z.boolean(), // §5.8 — rapprochement véhicule↔sortie
    crossingTool: z.boolean(), // §5.6
    turnbackSuggestion: z.boolean(), // §5.7 — false tant qu'aucun service d'itinéraire routier n'est câblé
    junctions: z.boolean(), // §5.1
    occupancy: z.boolean(), // D-005 — false tant que la source n'est pas confirmée par décodage protobuf réel
    depots: z.boolean(), // D-007 — false, aucune source identifiée
});
/** Seuils métier, serveur, configurables par réseau (CDC §5.5, D-003). */
export const Thresholds = z.object({
    deviationEarlySeconds: z.number(), // <= ce seuil (négatif) ⇒ « en avance »
    deviationLateSeconds: z.number(), // >= ce seuil ⇒ « en retard »
    deviationHighLateSeconds: z.number(), // priorité haute dans la file d'écarts
    bunchingHeadwayRatio: z.number().positive(), // intervalle réel < ratio × théorique ⇒ paire de bunching
    stalePositionSeconds: z.number().positive(), // position figée plus longtemps ⇒ positionType "stale"
    /**
     * Bornes propres à la ponctualité (§12.3), distinctes de la file d'écarts.
     * Modèle en 3 bandes aligné sur le tableau de bord exploitant (D-023) :
     *   exclu ← `avanceFloor` — **avance** — `early` — **conforme** — `late` — **retard** — `retardCeiling` → exclu
     */
    punctualityEarlySeconds: z.number(), // écart > ce seuil (côté avance) ⇒ conforme
    punctualityLateSeconds: z.number(), // écart < ce seuil ⇒ conforme
    /**
     * D-023 — bornes externes des bandes avance/retard. Un écart ≤ `avanceFloor`
     * (trop en avance) ou ≥ `retardCeiling` (trop en retard) est **exclu** de
     * l'analyse — comme les arrêts provisoires SAE côté exploitant : ce n'est plus
     * un défaut de ponctualité mais un incident, compté ailleurs.
     */
    punctualityAvanceFloorSeconds: z.number().default(-180), // écart ≤ ça ⇒ exclu (avance ≥ 3 min)
    punctualityRetardCeilingSeconds: z.number().default(600), // écart ≥ ça ⇒ exclu (retard ≥ 10 min)
    /**
     * Phase 2 — seuils d'**affichage** de l'onglet « Général » du panneau
     * Avance/Retard (P2-11). Purement frontend : une ligne y apparaît si son
     * écart est ≤ `generalTabEarlySeconds` OU ≥ `generalTabLateSeconds`. Ne
     * change **aucun** calcul serveur (déclassement `stale`, priorité de la file,
     * bunching gardent les seuils ci-dessus — décision D5).
     */
    generalTabEarlySeconds: z.number().default(-180),
    generalTabLateSeconds: z.number().default(480),
    /**
     * Phase 2 (P2-46) — au-delà de ce délai sans nouveau contact, un véhicule est
     * « hors ligne » dans l'onglet Véhicules ; au-delà de 24 h, « disparu ».
     * N'influe sur aucun calcul de course (le déclassement `stale` garde
     * `stalePositionSeconds`).
     */
    vehicleOfflineSeconds: z.number().positive().default(300),
    /**
     * Phase 2 (P2-18) — durée minimale d'une coupure (pause légale conducteur).
     * La détection « relève / pause à risque » alerte si le retard fait tomber
     * une coupure du plan de service sous ce seuil. Politique d'exploitation,
     * pas une valeur légale — à valider par le responsable planning.
     */
    minLegalBreakSeconds: z.number().positive().default(1800),
    /**
     * v1.44 — moteur d'anticipation (« Aide à la décision »). Horizon de
     * projection : on regarde l'état probable du réseau dans ce délai.
     */
    anticipationHorizonSeconds: z.number().positive().default(900),
    /**
     * v1.44 — un intervalle **projeté** ≥ ce ratio × l'intervalle cible ⇒
     * « trou de desserte qui se forme ». Distinct de `bunchingHeadwayRatio`
     * (l'autre bord).
     */
    gapFormingHeadwayRatio: z.number().positive().default(1.8),
    /**
     * v1.44 — un retournement / HLP partiel n'absorbe raisonnablement le retard
     * que jusqu'à cette valeur ; au-delà la manœuvre est marquée `partial`.
     */
    maxRecoverySeconds: z.number().positive().default(480),
    /**
     * v1.45 — outil de répartition des sorties (« déshabiller une sortie »).
     * Fenêtre en deçà de laquelle un véhicule est jugé « bientôt libre »
     * (dernière course de sa sortie qui se termine dans ce délai) et donc
     * candidat donneur.
     */
    reallocationDonorWindowSeconds: z.number().positive().default(2700),
    /**
     * v1.45 — en deçà de ce service restant, déshabiller une sortie cible n'en
     * vaut pas la peine (elle finit bientôt de toute façon).
     */
    reallocationMinTargetServiceSeconds: z.number().positive().default(1800),
    /**
     * v1.45 — au-delà de ce temps de HLP, la reprise est marquée
     * `feasibility: "uncertain"` (trop loin pour un dépannage réaliste).
     */
    reallocationMaxHlpSeconds: z.number().positive().default(1800),
    /**
     * v1.52 — **pause minimale légale d'un conducteur**, en secondes.
     *
     * Dans le service conducteur, la pause est le **trou entre la fin d'une
     * (ligne, sortie) et la prestation suivante** — les relèves en marquent les
     * bornes. En dessous de cette durée, la pause est considérée comme entamée :
     * c'est ce qui rend un échange de conducteurs acceptable ou non.
     *
     * Valeur réglementaire, pas une observation : elle se corrige **à chaud** via
     * `PUT /admin/networks/{n}` sans redéploiement. Astuce : 23 min (1380 s) au
     * 2026-09-13, susceptible de passer à 25 min.
     */
    driverMinimumBreakSeconds: z.number().positive().default(1380),
    /**
     * v1.53 — au-delà de cette durée, un trou dans le service conducteur n'est
     * plus une pause mais une **coupure** : le conducteur est hors service entre
     * deux moitiés de journée.
     *
     * Mesure Astuce (tous régimes, trous entre deux sorties) : 757 entre 23 et
     * 40 min, 141 entre 40 min et 1 h — ce sont des pauses ; puis 19 entre 1 et
     * 2 h, 58 entre 2 et 4 h, 47 au-delà — ce sont des services coupés. Défaut
     * 2 h, posé sur cette rupture. **À confirmer par l'exploitant** ; corrigeable
     * à chaud comme les autres seuils.
     */
    driverSplitShiftSeconds: z.number().positive().default(7200),
    /**
     * v1.54 — marge de rattrapage (secondes, de part et d'autre) pour rattacher
     * une course vue en temps réel mais hors de sa fenêtre théorique
     * (`[premier départ − 5 min ; dernier passage + 90 s]`) à son horaire plutôt
     * que de la traiter comme « ajoutée » (moteur B3, CDC §3.2). Sert
     * initialement à couvrir un bus parti un peu en avance ou en retard.
     *
     * Mesuré en prod le 2026-09-18 : sur 224 courses actives avec un prochain
     * arrêt horodaté, 156 (70 %) affichaient un horaire à plus d'1 h de
     * l'horodatage réel de la position — dont 124 à plus de 3 h (jusqu'à 16 h),
     * une ligne/tête de ligne visiblement fausse pour le régulateur (Cityway
     * republie le dernier `trip_id` connu d'un véhicule qui a terminé sa course
     * et fait autre chose — HLP le plus souvent). Sans borne, ce rattachement
     * acceptait n'importe quel `trip_id` vu en temps réel, aussi périmé soit-il.
     * Défaut 45 min (2700 s) : au-delà, la course retourne dans le lot « ajoutée
     * » (`addedTrip: true`, ligne dérivée du flux temps réel si connue, sinon
     * inconnue) plutôt que d'afficher une ligne/tête de ligne fausse.
     * Corrigeable à chaud comme les autres seuils.
     */
    strayTripRescueMarginSeconds: z.number().nonnegative().default(2700),
});
/**
 * Régimes d'exploitation nommés — **propres au réseau** (CDC §1.1). Le découpage
 * « lun-ven / samedi / dimanche+fériés / fermé » et ses noms (ROUGE/MAUVE/VERTE
 * pour Astuce) ne sont pas universels. `null` = le réseau n'a pas cette notion.
 */
export const OperatingPeriods = z.object({
    labels: z.object({
        weekday: z.string().nullable(),
        saturday: z.string().nullable(),
        sunday: z.string().nullable(),
        holiday: z.string().nullable(),
        closed: z.string().nullable(),
    }),
    today: z.string().nullable(),
});
/** Vocabulaire carrefours — propre au réseau (§12.5). */
export const JunctionVocabulary = z.object({
    techSystems: z.array(z.string()),
    operators: z.array(z.string()),
    statuses: z.array(z.string()),
});
export const MapStyle = z.object({
    id: z.string(),
    label: z.string(),
    url: z.string().url(),
});
/**
 * Emprise cartographique prédéfinie (D-011, §2) — pré-zoom sur un lieu clé
 * (hôtel de ville, pôle d'échange, dépôt, site remarquable). Donnée de réseau,
 * interdite en dur côté frontend (CDC §1.1). Optionnel : un réseau sans preset
 * n'affiche pas le sélecteur.
 */
export const MapPreset = z.object({
    id: z.string(),
    label: z.string(),
    bounds: BoundingBox,
});
/**
 * Exploitant opérationnel (backlog Phase 2 §2) — racine de la droplist
 * « Exploitants ». Distinct du **flux** GTFS-RT : une ligne du flux `TCAR:` peut
 * être exploitée par TNI, la TAD ou Calypso. `decisionScope: false` = affichée
 * mais hors file Avance/Retard, croisement et proposition de régulation.
 */
/**
 * Famille de lignes normalisée d'un exploitant (D-015) — plus fine que
 * `line.mode` : distingue TEOR et son **tronc commun** (T1+T2+T3), FAST, les
 * navettes, le scolaire, la TAD. Une ligne peut appartenir à **plusieurs**
 * familles (T1 est dans `teor` ET `tronc-commun`). Alimente l'arbre du bouton
 * de filtres « tri » de la carte (Exploitant → Famille → Lignes).
 */
export const ExploitantFamilyKind = z.enum([
    "metro",
    "teor",
    "tronc-commun",
    "fast",
    "bus",
    "navette",
    "scolaire",
    "tad",
]);
export const ExploitantFamily = z.object({
    id: z.string(),
    label: z.string(),
    kind: ExploitantFamilyKind,
    lineIds: z.array(z.string()),
    // D-015 — logo de la famille (TEOR, FAST…), SVG servi par l'API
    // (`GET /networks/{n}/assets/operator-icons/{file}`). `null` ⇒ pas de logo.
    logoUrl: z.string().nullable(),
});
export const Exploitant = z.object({
    id: z.string(),
    label: z.string(),
    decisionScope: z.boolean(),
    families: z.array(ExploitantFamily).default([]), // D-015
});
/**
 * Schéma de voies d'une ligne (backlog Phase 2 P2-29c) — « image ligne » fidèle
 * (le métro de Rouen : double voie V1/V2, jonction en Y). `assetUrl` : le SVG,
 * **servi par l'API** (D-013) — chemin relatif, à charger tel quel (`<img src>` /
 * `fetch`), rien à connaître d'avance côté front. `nodes` place chaque arrêt dans
 * le repère de `viewBox`. Le tronc / les branches viennent de `GET /thermometer`
 * (`directions[].branches`), pas d'ici.
 */
export const LineSchemaNode = z.object({ x: z.number(), y: z.number() });
export const LineSchema = z.object({
    lineId: z.string(),
    assetUrl: z.string(), // ex. "/api/v1/networks/astuce/assets/line-schemas/metro-rouen.svg"
    viewBox: z.string(), // "minX minY width height"
    nodes: z.record(z.string(), LineSchemaNode), // stopId → position sur le schéma
});
export const LineGroup = z.object({
    id: z.string(),
    label: z.string(),
    lineIds: z.array(z.string()),
    operatorId: z.string().nullable(), // v1.1 — flux GTFS-RT, assistance aux filtres (§12.10)
    exploitantId: z.string().nullable(), // Phase 2 — exploitant opérationnel (droplist « Exploitants »)
    punctualityTracked: z.boolean(), // v1.1 — inclus dans les rapports de ponctualité (§12.3)
});
/**
 * Réponse de `GET /networks/{n}/config`.
 * Tout le rendu client est piloté serveur (CDC §7.1) : rien n'est codé en dur côté frontend.
 */
export const NetworkConfigResponse = z.object({
    // Dérivé de `CONTRACT_VERSION` (common.ts), jamais un second littéral à la main —
    // sinon les deux dérivent silencieusement l'un de l'autre (trouvé lors de B3.2).
    contractVersion: z.literal(CONTRACT_VERSION),
    network: z.object({ id: z.string(), name: z.string() }),
    branding: z.object({
        displayName: z.string(),
        logoUrl: z.string().url().nullable(),
        primaryColor: z.string(),
    }),
    map: z.object({
        defaultBounds: BoundingBox,
        styles: z.array(MapStyle),
        defaultStyleId: z.string(),
        /** Pré-zooms sur des lieux clés (D-011). Absent ⇒ pas de sélecteur. */
        presets: z.array(MapPreset).optional(),
    }),
    /** Exploitants opérationnels (Phase 2) — racines de la droplist « Exploitants ». Vide ⇒ pas de regroupement par exploitant. */
    exploitants: z.array(Exploitant),
    /** Exploitant sélectionné à l'ouverture (`null` ⇒ le premier de `exploitants`). */
    defaultExploitantId: z.string().nullable(),
    lineGroups: z.array(LineGroup),
    defaultVisibleGroupIds: z.array(z.string()),
    /** Ligne présélectionnée sur l'écran Espace-temps ; null ⇒ le front choisit. */
    defaultSpacetimeLineId: z.string().nullable(),
    /** Schémas de voies par ligne (P2-29c). Vide ⇒ « image ligne » simple (barres). */
    lineSchemas: z.array(LineSchema).default([]),
    featureFlags: FeatureFlags,
    thresholds: Thresholds,
    /** v1.42 — dépôts du réseau (retour utilisateur 2026-09-10). Vide ⇒ pas de dépôt connu. */
    depots: z.array(z.object({
        id: z.string(),
        label: z.string(),
        lat: z.number(),
        lon: z.number(),
        radiusMeters: z.number(),
        scope: z.array(z.string()),
    })),
    /**
     * v1.42 — types de matériel par plage de n° de parc (retour utilisateur
     * 2026-09-10). Pour les badges véhicule et l'outil de répartition des
     * sorties. `class` : `standard` | `articulated` | `metro`. `teor` : `none`
     * (pas apte) | `capable` (habilité, peut hors-TEOR) | `only` (TEOR
     * uniquement). Un n° hors de toute plage ⇒ `vehicleTypeDefault`.
     */
    vehicleTypes: z.array(z.object({
        from: z.number().int(),
        to: z.number().int(),
        class: z.enum(["standard", "articulated", "metro"]),
        teor: z.enum(["none", "capable", "only"]),
        propulsion: z.enum(["diesel", "electric", "hydrogen"]),
        lineRestriction: z.array(z.string()),
        note: z.string().nullable(),
    })),
    vehicleTypeDefault: z.object({
        class: z.enum(["standard", "articulated", "metro"]),
        teor: z.enum(["none", "capable", "only"]),
        propulsion: z.enum(["diesel", "electric", "hydrogen"]),
    }),
    posts: z.object({ enabled: z.boolean() }),
    /**
     * v1.46 (D-020) — écran Véhicules : flux techniques masqués du parc par
     * défaut (TAE, navettes techniques…). `GET /vehicles` les retire, sauf pour
     * un poste qui régule ce flux (`?post=`). Le front en dérive aussi la
     * visibilité de la colonne « Exploitant / flux ».
     */
    fleetView: z.object({ hiddenOperatorIds: z.array(z.string()) }),
    operatingPeriods: OperatingPeriods, // v1.1 (§12.6)
    junctionVocabulary: JunctionVocabulary, // v1.1 (§12.5)
});
export const NetworksListResponse = z.object({
    networks: z.array(z.object({ id: z.string(), name: z.string(), enabled: z.boolean() })),
});
/** `GET /networks/{n}/posts` — liste vide = réseau sans notion de poste (CDC §5.0). */
export const PostsResponse = z.object({
    posts: z.array(z.object({
        id: z.string(),
        label: z.string(),
        operatorIds: z.array(z.string()),
        exploitantIds: z.array(z.string()), // Phase 2 — « P4 = tout TNI »
        lineIds: z.array(z.string()),
    })),
});
//# sourceMappingURL=config.js.map